import { useEffect } from 'react';
import type { ConversationDTO } from '@campus/core';
import { Button, Icon } from '../ds/index.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { useMembersStore, sessionInfo, loadMembers } from '../people/membersStore.js';
import { useMessagingStore, refreshConversations } from '../../messaging/store.js';
import { previewText, timeLabel } from './format.js';

/** Autre membre d'une conversation privée. */
export function otherMemberId(c: ConversationDTO): string | null {
  const me = sessionInfo().userId;
  return c.memberIds.find((id) => id !== me) ?? null;
}

/** Nom affiché d'une conversation (privé : nom de l'autre, dans l'annuaire). */
export function useConversationTitle(c: ConversationDTO | undefined): string {
  const otherId = c && c.kind === 'direct' ? otherMemberId(c) : null;
  const other = useMembersStore((s) => (otherId ? s.members.get(otherId) : undefined));
  if (!c) return '';
  if (c.kind === 'direct') return other?.displayName ?? c.lastMessage?.senderName ?? 'Membre';
  return c.title ?? 'Discussion';
}

function GroupIcon() {
  return (
    <span aria-hidden="true" style={{
      flexShrink: 0, width: 40, height: 40, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--color-primary-muted)', color: 'var(--color-primary-text)',
    }}><Icon name="users" size={18} /></span>
  );
}

function Row({ conv, onOpen }: { conv: ConversationDTO; onOpen: () => void }) {
  const title = useConversationTitle(conv);
  const otherId = conv.kind === 'direct' ? otherMemberId(conv) : null;
  const last = conv.lastMessage;
  const mine = last?.senderId === sessionInfo().userId;
  const author = !last || last.system ? '' : mine ? 'Toi : ' : conv.kind === 'direct' ? '' : `${last.senderName} : `;
  return (
    <li>
      <button type="button" className="pl-menu-item" onClick={onOpen}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 56, padding: '6px 8px', borderRadius: 'var(--radius-md)', textAlign: 'left' }}>
        {otherId ? <PersonAvatar userId={otherId} name={title} size={40} /> : <GroupIcon />}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: conv.unread > 0 ? 800 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
            {last && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{timeLabel(last.createdAt)}</span>}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontSize: 'var(--text-xs)', color: conv.unread > 0 ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {last ? `${author}${previewText(last)}` : 'Pas encore de message'}
            </span>
            {conv.unread > 0 && (
              <span className="pl-num pl-unread" aria-label={`${conv.unread} non lu${conv.unread > 1 ? 's' : ''}`}>
                {conv.unread >= 99 ? '99+' : conv.unread}
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

/** Messages perso : conversations privées et groupes, de la plus récente à la plus ancienne. */
export function ConversationList({ onOpen, onNew, onNewGroup }: { onOpen: (id: string) => void; onNew: () => void; onNewGroup: () => void }) {
  const conversations = useMessagingStore((s) => s.conversations);
  const loaded = useMessagingStore((s) => s.loaded);
  useEffect(() => { void loadMembers(); void refreshConversations(); }, []);

  const personal = conversations.filter((c) => c.kind === 'direct' || c.kind === 'group');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      {/* Côte à côte s'il y a la place, sinon l'un sous l'autre (panneau étroit, thème Campus). */}
      <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button variant="secondary" icon="plus" onClick={onNew} style={{ flex: '1 1 150px' }}>Nouveau message</Button>
        <Button variant="secondary" icon="users" onClick={onNewGroup} style={{ flex: '1 1 150px' }}>Nouveau groupe</Button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 16px' }}>
        {!loaded && conversations.length === 0 ? (
          <p className="pl-help" style={{ margin: 8 }}>Chargement…</p>
        ) : personal.length === 0 ? (
          <p className="pl-help" style={{ margin: '8px 8px' }}>
            Aucune conversation pour l&apos;instant. Écris à quelqu&apos;un, même hors ligne : il verra ton message à sa connexion.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {personal.map((c) => <Row key={c.id} conv={c} onOpen={() => onOpen(c.id)} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
